import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { RBarChart } from '../../../../ChartUtility/recharts';
import { colorAt } from '../../../../ChartUtility/recharts/_kit';
import { Field, Picker } from './ComparisonControls';
import { useComparisonSeries } from './useComparisonSeries';
import { perPeriod } from '../../utils/comparisonSeries';
import { meterOptions, kpiOfMeter } from '../../utils/meterKpis';
import { financialYearMonths } from '../../utils/comparisonSeries';
import { useMonthAcrossYears } from './useMonthAcrossYears';

/**
 * A period comparison, which is not a two-group split but N bars.
 *
 * "Which month was worst?" is not a question about two groups, and forcing it
 * into Group 1 versus Group 2 would answer it two months at a time. The reader
 * picks a financial year and the months within it, and gets one bar each.
 *
 * Two questions, not one. "Which month was worst this year?" gives a bar per
 * month of one year; "how does July compare year on year?" gives a bar per
 * year of one month. Carbon Footprinting splits them the same way, and a
 * reader moving between the two pages should not have to learn it twice.
 *
 * The Financial Year dimension also compares years, but two at a time and
 * across every month. This answers a narrower question — one month, every year
 * — which that cannot.
 */

/** One colour for all bars — the categories are periods, not different things. */
const BAR_COLOR = colorAt(0);

/** The two questions this view answers. */
const ACROSS_YEARS = 'acrossYears';
const IN_YEAR = 'inYear';

/**
 * The figure is chosen by meter rather than by KPI, as everywhere else on this
 * tab: a reader knows which meter they want to look at, and would have to know
 * which KPI it happens to be mapped to this year to find it otherwise.
 *
 * @param {React.ReactNode} controls - The Compare by switch, rendered in the head.
 * @param {Array<object>} meters - Meters, with their mappings per year.
 * @param {Array<object>} financialYearOptions - Years the company reports on.
 * @param {Array<object>} sourceOptions - Locations to scope the figures to.
 */
const PeriodComparison = ({
  controls,
  meters = [],
  financialYearOptions = [],
  sourceOptions = [],
}) => {
  const [mode, setMode] = useState(ACROSS_YEARS);
  const [financialYear, setFinancialYear] = useState(financialYearOptions?.[0]?.value ?? '');
  const [location, setLocation] = useState('');
  const [meterId, setMeterId] = useState('');
  const [month, setMonth] = useState('');

  const options = useMemo(() => meterOptions(meters), [meters]);
  const meter = options.find((option) => option.value === meterId) || null;

  /**
   * The KPI the chosen meter is mapped to in the year being read. A meter
   * carries one per year, so the year decides it rather than the meter alone.
   */
  const kpi = useMemo(
    () => kpiOfMeter(meters, meterId, financialYear),
    [meters, meterId, financialYear]
  );

  /** Named for the months of this company's own year, not the calendar's. */
  const monthOptions = useMemo(
    () =>
      financialYearMonths().map((label, index) => ({
        value: String(index),
        label,
      })),
    []
  );

  const years = useMemo(
    () => financialYearOptions.map((option) => option.value),
    [financialYearOptions]
  );

  const inYear = useComparisonSeries({
    kpi,
    meterIds: meterId ? [meterId] : undefined,
    financialYear,
    locationId: location,
    timeRange: 'thisFY',
    aggregation: 'month',
    enabled: mode === IN_YEAR && Boolean(financialYear && meterId && kpi),
  });

  const acrossYears = useMonthAcrossYears({
    meters,
    meterId,
    financialYears: years,
    month,
    locationId: location,
    enabled: mode === ACROSS_YEARS && month !== '' && Boolean(meterId),
  });

  const { unit, isLoading, error } =
    mode === ACROSS_YEARS ? acrossYears : inYear;
  const rows = inYear.rows;

  /** One bar per month the year reported in, oldest first. */
  const monthBars = useMemo(
    () =>
      perPeriod(rows).map((point) => ({
        name: moment(point.ts).format('MMM YYYY'),
        value: point.value,
      })),
    [rows]
  );

  const bars = mode === ACROSS_YEARS ? acrossYears.bars : monthBars;

  /** What the bars are of, for the caption over the chart. */
  const axis =
    mode === ACROSS_YEARS
      ? `${monthOptions[Number(month)]?.label || 'One month'} · by financial year`
      : `${financialYear || 'One year'} · by month`;

  return (
    <>
      <section className="wc-panel">
        <div className="wc-panel-head">
          <div>
            <h3 className="wc-panel-title">Filters</h3>
            <span className="wc-panel-sub">
              A month across years, or months within a year
            </span>
          </div>
          {controls}
        </div>

        <div className="wc-modes">
          <div className="wc-toggle" role="tablist" aria-label="Period mode">
            <button
              type="button"
              role="tab"
              aria-selected={mode === ACROSS_YEARS}
              className={`wc-toggle-btn${mode === ACROSS_YEARS ? ' wc-toggle-btn--on' : ''}`}
              onClick={() => setMode(ACROSS_YEARS)}
            >
              Same month · across years
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === IN_YEAR}
              className={`wc-toggle-btn${mode === IN_YEAR ? ' wc-toggle-btn--on' : ''}`}
              onClick={() => setMode(IN_YEAR)}
            >
              Months · within a year
            </button>
          </div>
        </div>

        <div className="wc-filters">
          {mode === ACROSS_YEARS ? (
            <Field label="Month" htmlFor="wc-period-month">
              <Picker
                id="wc-period-month"
                value={month}
                onChange={setMonth}
                options={monthOptions}
                placeholder="Select month…"
              />
            </Field>
          ) : (
            <Field label="Financial Year" htmlFor="wc-period-fy">
              <Picker
                id="wc-period-fy"
                value={financialYear}
                onChange={setFinancialYear}
                options={financialYearOptions}
                placeholder="Select FY…"
              />
            </Field>
          )}

          <Field label="Meter" htmlFor="wc-period-meter">
            <Picker
              id="wc-period-meter"
              value={meterId}
              onChange={setMeterId}
              options={options}
              placeholder="Select meter…"
            />
          </Field>

          <Field label="Location" htmlFor="wc-period-loc" common>
            <Picker
              id="wc-period-loc"
              value={location}
              onChange={setLocation}
              options={sourceOptions}
              placeholder="All Locations"
            />
          </Field>
        </div>
      </section>

      <section className="wc-panel">
        <div className="wc-panel-head">
          <div>
            <h3 className="wc-panel-title">{meter?.label || 'Period comparison'}</h3>
            <span className="wc-panel-sub">{axis} · {unit}</span>
          </div>
        </div>

        {error ? (
          <p className="wc-empty wc-empty--bad" role="alert">
            The request for these months failed, so there is nothing to compare.
          </p>
        ) : isLoading ? (
          <div className="wc-skeleton" aria-label="Loading period comparison" />
        ) : bars.length ? (
          <RBarChart
            data={bars}
            xKey="name"
            series={[{ key: 'value', name: meter?.label || 'Total', color: BAR_COLOR, maxBarSize: 56 }]}
            unit={unit}
            height={320}
            animate
          />
        ) : (
          <p className="wc-empty">
            {mode === ACROSS_YEARS
              ? 'Pick a month to compare it across financial years.'
              : 'No month in this financial year reported anything.'}
          </p>
        )}
      </section>
    </>
  );
};

export default PeriodComparison;
